import { useState } from 'react';
import { Badge, Button, EmptyState, InlineError, Skeleton, Textarea } from '../../components/ui';
import { useEffectiveSupplierOrg, useMimic } from '../supplierOrganizations/MimicMode';
import { useAcknowledgeHandoff, useHandoffLinks, useHandoffs, useSubmissions } from './hooks';
import { NewSubmissionDialog } from './NewSubmissionDialog';
import { useSubmissionOptionCounts } from './sampleReview';
import { SampleApproval } from './SampleApproval';
import { SupplierSampleSlots } from './SupplierSampleSlots';
import { groupSubmissions } from './submissionGroups';
import {
  SUBMISSION_STATUS_LABELS,
  SUBMISSION_STATUS_TONES,
  exchangeErrorMessage,
  splitTags,
} from './types';
import type { SubmissionGroup } from './submissionGroups';
import type { SupplierHandoff, SupplierSubmission } from './types';

/**
 * The supplier-facing half of the exchange, rendered as tabs inside the
 * portal. Row Level Security scopes every list to the supplier's own
 * organization; these components are plain reads and writes.
 */

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * One run, closed by default.
 *
 * Every submission used to render as a full panel with all four component
 * cards expanded underneath it, which is what made this page a scroll rather
 * than a list. Closed, a run is a single line: what it is, where it stands,
 * when it went, how many photos are on it. That last number is why the counts
 * are fetched for the whole page at once — see useSubmissionOptionCounts.
 *
 * The body is not merely hidden when closed, it is not mounted. A native
 * <details> renders its children either way, and SupplierSampleSlots opens two
 * queries per run — so a factory with thirty runs would fire sixty requests to
 * draw a list nobody had expanded yet.
 */
function SubmissionRow({
  submission,
  photoCount,
}: {
  submission: SupplierSubmission;
  /** Null while the page-wide count query is still in flight. */
  photoCount: number | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const { canWrite } = useMimic();

  const canUpload =
    canWrite && (submission.status === 'submitted' || submission.status === 'revision_requested');

  return (
    <details
      className="submission-row"
      open={expanded}
      onToggle={(event) => setExpanded(event.currentTarget.open)}
    >
      <summary className="submission-row-summary">
        <span className="submission-row-title">{submission.title}</span>
        <Badge tone={SUBMISSION_STATUS_TONES[submission.status]}>
          {SUBMISSION_STATUS_LABELS[submission.status]}
        </Badge>
        <span className="submission-row-date">sent {shortDate(submission.created_at)}</span>
        <span className="submission-row-count">
          {photoCount === null
            ? '—'
            : `${String(photoCount)} ${photoCount === 1 ? 'photo' : 'photos'}`}
        </span>
      </summary>

      {expanded ? (
        <div className="submission-row-body">
          {submission.notes === '' ? null : (
            <p className="submission-row-notes">{submission.notes}</p>
          )}
          {submission.decision_note === '' ? null : (
            <p className="submission-row-decision">
              <strong>TAP:</strong> {submission.decision_note}
            </p>
          )}
          {/* The component-by-component view. Uploads open only where TAP is
              actually waiting on this factory; everything else is read-only,
              and the database refuses the rest (guard_option_insert). */}
          <SampleApproval
            status={submission.status}
            decidedBy={submission.decided_by}
            decidedAt={submission.decided_at}
          >
            <SupplierSampleSlots submissionId={submission.id} canUpload={canUpload} />
          </SampleApproval>
        </div>
      ) : null}
    </details>
  );
}

/**
 * One collapsible group of submissions — the pinned action group, or a month.
 *
 * A native <details> rather than a hand-rolled disclosure: it is keyboard
 * operable and announced correctly for free, and the browser's own
 * find-in-page opens it when the match is inside.
 */
function SubmissionGroupSection({
  group,
  counts,
}: {
  group: SubmissionGroup;
  counts: Map<string, number> | null;
}) {
  return (
    <details className="submission-group" open={group.openByDefault}>
      <summary className="submission-group-summary">
        <span className="submission-group-name">{group.name}</span>
        <span className="submission-group-count">
          {group.submissions.length} {group.submissions.length === 1 ? 'run' : 'runs'}
        </span>
      </summary>
      {group.hint === '' ? null : <p className="submission-group-hint">{group.hint}</p>}
      {group.submissions.map((submission) => (
        <SubmissionRow
          key={submission.id}
          submission={submission}
          photoCount={counts === null ? null : (counts.get(submission.id) ?? 0)}
        />
      ))}
    </details>
  );
}

export function PortalSubmissions() {
  const submissionsQuery = useSubmissions();
  const [creating, setCreating] = useState(false);

  // Carried, not inferred — a mimicking super admin has no organization on
  // their own profile, so reading it there would submit with an empty id.
  const organizationId = useEffectiveSupplierOrg();
  // Read-only unless a mimicking super admin has switched writing on. A real
  // supplier always gets true, so nothing changes for the people whose portal
  // this actually is.
  const { canWrite } = useMimic();

  const submissions = submissionsQuery.data ?? [];
  // One request for the whole page. Hooks cannot be called after the pending
  // return below, so this runs on every render and simply stays disabled while
  // the list is empty.
  const countsQuery = useSubmissionOptionCounts(submissions.map((submission) => submission.id));

  if (submissionsQuery.isPending) return <Skeleton lines={3} label="Loading submissions" />;

  // Approved runs are deliberately absent: they are finished, and leaving them
  // here was most of what made this page unreadable. They live under Completed.
  // Today is passed in UTC to match created_at, which decides the buckets.
  const groups = groupSubmissions(submissions, new Date().toISOString().slice(0, 10));
  const counts = countsQuery.data ?? null;

  // The stock report used to be rendered here as well as by the portal's own
  // inventory section, so the Inventory tab drew the panel TWICE — two "No
  // stock report uploaded yet" lines and two Upload buttons, one above the
  // other. It was right when this component was the only place a supplier
  // could reach their stock; the portal tab made it a duplicate and nothing
  // removed it. The tab owns the panel now.
  return (
    <div>
      <div className="submission-feed-bar">
        <h3 className="project-category-heading">Sample submissions</h3>
        {canWrite ? (
          <Button onClick={() => setCreating(true)}>+ New submission</Button>
        ) : (
          <span className="submission-feed-readonly">
            Read-only preview. Turn on test writing in the Mimic Mode bar to send a submission as
            this supplier.
          </span>
        )}
      </div>

      <NewSubmissionDialog
        open={creating}
        organizationId={organizationId}
        onClose={() => setCreating(false)}
      />

      {groups.length === 0 ? (
        <EmptyState
          title={submissions.length === 0 ? 'No submissions yet' : 'Nothing open right now'}
          description={
            submissions.length === 0
              ? 'Send your first sample submission to TAP.'
              : 'Everything you have sent has been approved. Finished runs are under Completed.'
          }
        />
      ) : (
        groups.map((group) => (
          <SubmissionGroupSection key={group.key} group={group} counts={counts} />
        ))
      )}
    </div>
  );
}

function HandoffCard({ handoff }: { handoff: SupplierHandoff }) {
  const linksQuery = useHandoffLinks(handoff.id);
  const acknowledge = useAcknowledgeHandoff();
  const { canWrite } = useMimic();
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const links = linksQuery.data ?? [];
  const tags = splitTags(handoff.tags);
  const acknowledged = handoff.acknowledged_at !== null;

  return (
    <div className="ui-panel" style={{ marginTop: 'var(--space-4)' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)' }}
      >
        <strong>{handoff.title}</strong>
        <Badge tone={acknowledged ? 'success' : 'warning'}>
          {acknowledged ? 'Acknowledged' : 'Needs acknowledgement'}
        </Badge>
        {tags.map((tag) => (
          <Badge key={tag} tone="neutral">
            {tag}
          </Badge>
        ))}
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
          {shortDate(handoff.created_at)}
        </span>
      </div>
      {handoff.notes !== '' && (
        <p style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--font-size-sm)' }}>
          {handoff.notes}
        </p>
      )}
      {links.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-3)',
            marginTop: 'var(--space-3)',
          }}
        >
          {links.map((link) => (
            <a key={link.id} href={link.url} target="_blank" rel="noreferrer">
              {link.name === '' ? link.url : link.name}
            </a>
          ))}
        </div>
      )}
      {error !== '' && <InlineError>{error}</InlineError>}
      {!canWrite && !acknowledged ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
          Read-only preview. Acknowledging would be recorded as this supplier.
        </p>
      ) : acknowledged ? (
        handoff.ack_note !== '' && (
          <p
            style={{
              margin: 'var(--space-3) 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-secondary)',
            }}
          >
            <strong>Your note:</strong> {handoff.ack_note}
          </p>
        )
      ) : (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Textarea
            label="Note to TAP (optional)"
            rows={2}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <Button
            disabled={acknowledge.isPending}
            onClick={() => {
              setError('');
              acknowledge.mutate(
                { handoffId: handoff.id, note },
                { onError: (cause) => setError(exchangeErrorMessage(cause.message)) },
              );
            }}
          >
            {acknowledge.isPending ? 'Acknowledging…' : 'Acknowledge'}
          </Button>
        </div>
      )}
    </div>
  );
}

export function PortalHandoffs() {
  const handoffsQuery = useHandoffs();
  if (handoffsQuery.isPending) return <Skeleton lines={3} label="Loading" />;
  const handoffs = handoffsQuery.data ?? [];

  return (
    <div>
      <p style={{ margin: '0 0 var(--space-4)', color: 'var(--text-secondary)' }}>
        Final designs approved by the client. Review each one and acknowledge it — add a note if
        anything needs raising before production.
      </p>
      {handoffs.length === 0 ? (
        <EmptyState title="Nothing waiting" description="TAP has not sent anything yet." />
      ) : (
        handoffs.map((handoff) => <HandoffCard key={handoff.id} handoff={handoff} />)
      )}
    </div>
  );
}
